"use client";

import React from "react";
import { Loader, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const inputClasses = "flex w-full bg-transparent border-0 border-b border-primary/50 px-0 py-3 text-base text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-lg transition-colors appearance-none";
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center h-full space-y-6 animate-in fade-in duration-500">
        <h3 className="text-2xl font-default font-bold text-primary">
          Estimate Request Sent
        </h3>
        <p className="text-white/70 text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 bg-transparent text-primary border-primary/30 hover:bg-primary hover:text-black px-8 py-6 text-base font-default font-semibold rounded-lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field,
        fieldState
      }) => <FormItem>
              <FormLabel className="text-white/70 uppercase tracking-widest text-xs font-default font-bold">
                Full Name *
              </FormLabel>
              <FormControl>
                <input type="text" placeholder="Enter your name" className={cn(inputClasses, fieldState.error && "border-red-500 text-red-500 placeholder:text-red-500/50")} {...field} />
              </FormControl>
              <FormMessage className="font-bold text-base" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field,
        fieldState
      }) => <FormItem>
              <FormLabel className="text-white/70 uppercase tracking-widest text-xs font-default font-bold">
                Email Address *
              </FormLabel>
              <FormControl>
                <input type="email" placeholder="email@example.com" className={cn(inputClasses, fieldState.error && "border-red-500 text-red-500 placeholder:text-red-500/50")} {...field} />
              </FormControl>
              <FormMessage className="font-bold text-base" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field,
        fieldState
      }) => <FormItem>
              <FormLabel className="text-white/70 uppercase tracking-widest text-xs font-default font-bold">
                Phone Number *
              </FormLabel>
              <FormControl>
                <input type="tel" placeholder="(555) 000-0000" className={cn(inputClasses, fieldState.error && "border-red-500 text-red-500 placeholder:text-red-500/50")} {...field} />
              </FormControl>
              <FormDescription className="text-white/40 text-xs mt-1">
                Required for scheduling your site visit.
              </FormDescription>
              <FormMessage className="font-bold text-base" />
            </FormItem>} />

        <FormField control={control} name="reason" rules={{
        required: "Please select a service"
      }} render={({
        field,
        fieldState
      }) => <FormItem>
              <FormLabel className="text-white/70 uppercase tracking-widest text-xs font-default font-bold">
                Primary Service *
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={cn(inputClasses, fieldState.error && "border-red-500 text-red-500")} {...field}>
                    <option value="" disabled className="text-black">
                      Select a service
                    </option>
                    {["Roof Replacement", "Roof Repair", "Siding Installation", "Gutter Installation", "Storm Damage Repair"].map((opt, i) => <option key={i} data-index={i} value={opt} className="text-black bg-white">
                        {opt}
                      </option>)}
                  </select>
                  <ChevronDown className={cn("absolute right-0 top-1/2 -translate-y-1/2 size-4 pointer-events-none", fieldState.error ? "text-red-500" : "text-primary")} />
                </div>
              </FormControl>
              <FormMessage className="font-bold text-base" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Project details are required"
    }} render={({
      field,
      fieldState
    }) => <FormItem>
            <FormLabel className="text-white/70 uppercase tracking-widest text-xs font-default font-bold">
              Project Details *
            </FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your exterior needs..." className={cn(inputClasses, "min-h-[120px] resize-y", fieldState.error && "border-red-500 text-red-500 placeholder:text-red-500/50")} {...field} />
            </FormControl>
            <FormMessage className="font-bold text-base" />
          </FormItem>} />

      <input name="website_url" type="hidden" aria-hidden="true" style={{
      display: 'none'
    }} tabIndex={-1} autoComplete="off" />

      {submitError && <div className="p-4 bg-red-950/50 border border-red-900 text-red-200 text-sm font-default rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-black hover:bg-white hover:text-black border border-primary px-12 py-7 text-lg font-default font-bold transition-all duration-300 uppercase tracking-widest rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-3 size-5" />
            Processing...
          </> : "Get My Free Estimate & Pricing"}
      </Button>
    </Form>;
}
export default function ConnectWithUs() {
  return <section data-section-id="240594" id="request-your-free-estimate" className="dark bg-background text-foreground w-full min-h-screen flex items-center py-24">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 w-full">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          <div className="lg:w-[40%] flex flex-col justify-center">
            <h2 className="text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.9] font-bold uppercase tracking-tighter font-default text-white">
              Get Your<br />
              Free Expert<br />
              Assessment
            </h2>
          </div>

          <div className="lg:w-[60%] flex flex-col justify-center">
            <FormProvider formKey="free_estimate_form_home" formId="free_estimate_form_home" sectionName="Request Your Free Estimate" defaultValues={{
            name: "",
            email: "",
            phone: "",
            reason: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            reason: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Get My Free Estimate & Pricing" successMessage="Thank you for choosing Eagle Exteriors. Our specialists will contact you within 24 hours to schedule your free inspection.">
              <div className="mb-8 space-y-2">
                <p className="text-primary font-bold uppercase tracking-widest text-sm">Your information is 100% secure. No pressure, no obligation.</p>
                <p className="text-white/60 text-sm">Response time: usually within 2 hours on weekdays</p>
              </div>
              <ConnectForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}